import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { FEATURES } from "@/lib/features";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/components/RequireRole";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const FeaturesMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger className="hidden items-center gap-1 text-sm text-slate-300 outline-none hover:text-white sm:flex" data-testid="public-nav-features">
      Features <ChevronDown className="h-3.5 w-3.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-60">
      <DropdownMenuItem asChild><Link to="/features" data-testid="public-nav-features-all">All features</Link></DropdownMenuItem>
      <DropdownMenuSeparator />
      {FEATURES.map(({ slug, title, icon: Icon }) => (
        <DropdownMenuItem key={slug} asChild>
          <Link to={`/features/${slug}`} className="flex items-center gap-2" data-testid={`public-nav-feature-${slug}`}><Icon className="h-4 w-4 text-blue-700" />{title}</Link>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const PublicNav = () => {
  const { profile } = useAuth();
  return (
  <header className="absolute inset-x-0 top-0 z-20">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
      <Link to="/" className="flex items-center gap-2 text-white" data-testid="home-logo"><img src="/img/logo-icon.png" alt="" className="h-8 w-8 object-contain" /><span className="font-display whitespace-nowrap text-base font-extrabold sm:text-lg">Warrant Whizz</span></Link>
      <nav aria-label="Main" className="flex items-center gap-2 sm:gap-5">
        <FeaturesMenu />
        <Link to="/features" className="px-1 text-sm text-slate-300 hover:text-white sm:hidden" data-testid="public-nav-features-mobile">Features</Link>
        <Link to="/#pricing" className="hidden text-sm text-slate-300 hover:text-white sm:inline" data-testid="home-nav-pricing">Pricing</Link>
        <Link to="/#questions" className="hidden text-sm text-slate-300 hover:text-white md:inline" data-testid="home-nav-questions">Questions</Link>
        {profile ? (
          <Button asChild className="bg-blue-600 px-3 hover:bg-blue-500 sm:px-4" data-testid="public-nav-open-app"><Link to={homeFor(profile)}>Open app</Link></Button>
        ) : (<>
          <Button asChild variant="ghost" className="px-2 text-white hover:bg-white/10 hover:text-white sm:px-4" data-testid="home-login-button"><Link to="/login">Log in</Link></Button>
          <Button asChild className="bg-blue-600 px-3 hover:bg-blue-500 sm:px-4" data-testid="home-signup-button"><Link to="/signup"><span className="sm:hidden">Trial</span><span className="hidden sm:inline">Start free trial</span></Link></Button>
        </>)}
      </nav>
    </div>
  </header>
  );
};

export const PublicFooter = () => (
  <footer className="border-t border-slate-800 bg-slate-950 py-12" data-testid="home-footer">
    <div className="mx-auto max-w-6xl px-6">
      <nav aria-label="Features" className="flex flex-wrap gap-x-6 gap-y-2 text-sm" data-testid="footer-feature-links">
        <Link to="/features" className="font-semibold text-white hover:text-blue-400">Features</Link>
        {FEATURES.map((f) => <Link key={f.slug} to={`/features/${f.slug}`} className="text-slate-400 hover:text-blue-400" data-testid={`footer-feature-${f.slug}`}>{f.title}</Link>)}
      </nav>
      <div className="mt-8 flex flex-col gap-2 text-sm text-slate-400 sm:flex-row sm:justify-between">
        <p data-testid="home-footer-copyright">© {new Date().getFullYear()} Warrant Whizz · A product by{" "}
          <a href="https://newhamemergencylocksmiths.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-blue-400 hover:underline" data-testid="home-footer-newham-link">Newham Emergency Locksmiths</a>
        </p>
        <p>Questions? <a href="mailto:michael@newhamemergencylocksmiths.co.uk" className="font-medium text-blue-400 hover:underline" data-testid="home-footer-email">michael@newhamemergencylocksmiths.co.uk</a></p>
      </div>
    </div>
  </footer>
);
